<template lang="pug">
v-dialog(v-model="bannerShow" max-width="400" content-class="full-screen-banner-dialog" persistent transition="dialog-bottom-transition")
  v-card(v-if="currentBanner" elevation="0" color="transparent")
    .full-screen-banner
      .banner-container
        img(:src="currentBanner.src")
        .close-button(@click="closeBanner()")
          .close-icon
        v-btn.action-button(
          v-if="currentBanner.link"
          @click="openBanner()"
          rounded-xl
          elevation="2"
          :style="{ 'background-color': currentBanner.linkBackground + ' !important', 'color': currentBanner.linkColor + ' !important' }"
        ) {{ currentBanner.linkText }}

</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import { getLocale } from '../../../i18n'
import { DIALOG_PRIORITIES } from '../../../utils/dialogPriorities'

const DIALOG_ID = 'full-screen-banner'

export default {
  data: () => ({
    fullScreenBannersJSON: null,
    currentBanner: null,
    showBanner: false,
  }),

  computed: {
    ...mapGetters('dialogQueue', ['isActive']),

    locale() {
      return getLocale()
    },

    bannerShow() {
      return this.showBanner && this.isActive(DIALOG_ID)
    },
  },

  mounted() {
    this.firebaseRemoteConfigGetString('FULLSCREEN_BANNERS').then((result) => {
      try {
        this.fullScreenBannersJSON = JSON.parse(result)[this.locale]
        this.selectCurrentBanner()
      } catch (error) {
        // console.debug(error)
      }
    })
  },

  beforeDestroy() {
    this.releaseDialog(DIALOG_ID)
  },

  methods: {
    ...mapActions({
      firebaseRemoteConfigGetString: 'tools/firebaseRemoteConfigGetString',
      analyticsLogEvent: 'analytics/logEvent',
      requestDialog: 'dialogQueue/request',
      releaseDialog: 'dialogQueue/release',
    }),

    selectCurrentBanner() {
      if (!this.fullScreenBannersJSON || !Array.isArray(this.fullScreenBannersJSON)) {
        return
      }

      const today = new Date()
      const closedBanners = this.getClosedBanners()

      // Фильтруем баннеры по актуальным датам
      const activeBanners = this.fullScreenBannersJSON.filter(banner => {
        const startDate = new Date(banner.startDate)
        const endDate = new Date(banner.endDate)
        return today >= startDate && today <= endDate
      })

      // Сортируем по приоритету (ascending - меньшее число = выше приоритет)
      activeBanners.sort((a, b) => a.priority - b.priority)

      // Ищем первый баннер, который не закрыт пользователем
      const selectedBanner = activeBanners.find(banner => !closedBanners.includes(banner.id))

      this.currentBanner = selectedBanner || null
      this.showBanner = !!selectedBanner

      if (selectedBanner) {
        this.requestDialog({ id: DIALOG_ID, priority: DIALOG_PRIORITIES.BANNERS })

        // Закрываем баннер через 20 секунд
        setTimeout(() => {
          this.closeBanner()
        }, 20000)
        this.analyticsLogEvent({
          eventName: 're_dash_fullscreenbanner_showed',
          eventProperties: {
            fullscreenbanner_name: this.currentBanner.id,
            fullscreenbanner_start_date: this.$moment(this.currentBanner.startDate).format('DD-MM-YYYY'),
            fullscreenbanner_end_date: this.$moment(this.currentBanner.endDate).format('DD-MM-YYYY'),
            fullscreenbanner_link: this.currentBanner.link,
          },
        })
      }
    },

    getClosedBanners() {
      try {
        const closed = localStorage.getItem('closedFullScreenBanners')
        return closed ? JSON.parse(closed) : []
      } catch (error) {
        return []
      }
    },

    addToClosedBanners(bannerId) {
      try {
        const closedBanners = this.getClosedBanners()
        if (!closedBanners.includes(bannerId)) {
          closedBanners.push(bannerId)
          localStorage.setItem('closedFullScreenBanners', JSON.stringify(closedBanners))
        }
      } catch (error) {
        // console.debug('Error saving closed banner:', error)
      }
    },

    openBanner() {
      if (this.currentBanner.link) {
        this.$openLink(this.currentBanner.link, '_system')
        this.analyticsLogEvent({
          eventName: 're_dash_fullscreenbanner_link_clicked',
          eventProperties: {
            fullscreenbanner_name: this.currentBanner.id,
            fullscreenbanner_start_date: this.$moment(this.currentBanner.startDate).format('DD-MM-YYYY'),
            fullscreenbanner_end_date: this.$moment(this.currentBanner.endDate).format('DD-MM-YYYY'),
            fullscreenbanner_link: this.currentBanner.link,
          },
        })
      }
    },

    closeBanner() {
      if (this.currentBanner) {
        this.addToClosedBanners(this.currentBanner.id)

        this.analyticsLogEvent({
          eventName: 're_dash_fullscreenbanner_closed',
          eventProperties: {
            fullscreenbanner_name: this.currentBanner.id,
            fullscreenbanner_start_date: this.$moment(this.currentBanner.startDate).format('DD-MM-YYYY'),
            fullscreenbanner_end_date: this.$moment(this.currentBanner.endDate).format('DD-MM-YYYY'),
            fullscreenbanner_link: this.currentBanner.link,
          },
        })

        this.showBanner = false
        this.currentBanner = null
        this.releaseDialog(DIALOG_ID)
      }
    },
  },
}
</script>
<style lang="stylus">
.full-screen-banner
  .banner-container
    position relative
    display inline-block

    img
      display block
      border-radius 16px
      max-width 100%
      height auto
      max-height 90vh

    .close-button
      position absolute
      top 16px
      right 16px
      width 24px
      height 24px
      background rgba(255, 255, 255, 1)
      border-radius 50%
      cursor pointer
      display flex
      align-items center
      justify-content center
      z-index 10
      transition background 0.3s ease

      &:hover
        background rgba(255, 255, 255, 0.8)

      .close-icon
        position relative
        width 12px
        height 12px

        &::before,
        &::after
          content ''
          position absolute
          top 50%
          left 50%
          width 15px
          height 2px
          background #000000
          border-radius 1px
          transform-origin center

        &::before
          transform translate(-50%, -50%) rotate(45deg)

        &::after
          transform translate(-50%, -50%) rotate(-45deg)

    .action-button
      position absolute
      bottom 30px
      left 50%
      transform translateX(-50%)
      padding 12px 24px
      font-size 16px
      font-weight 600
      width 80%
      z-index 10

.full-screen-banner-dialog
  max-height 100% !important

// Подложка баннера должна остаться прозрачной - глобальное правило красит
// v-sheet внутри светлых диалогов в белый (main.styl), перебиваем специфичностью
.v-dialog.full-screen-banner-dialog .v-card.v-sheet
  background-color transparent !important
</style>
